import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import { apiFetch } from '../lib/api';

const defaultManualEntry = {
  name: '',
  position: '',
  company: '',
  location: '',
  email: '',
  linkedin: '',
};

const useConnectCRM = ({ userId, showToast, ensureUserRecordExists, currentChatId }) => {
  const [savedAlumniRows, setSavedAlumniRows] = useState([]);
  const [userCategories, setUserCategories] = useState([]);
  const [categories, setCategories] = useState({});
  const [comments, setComments] = useState({});
  const [expandedCells, setExpandedCells] = useState({});
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState('all');
  const [filters, setFilters] = useState({});
  const [openFilterDropdown, setOpenFilterDropdown] = useState(null);
  const [pendingFilters, setPendingFilters] = useState({});
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [manualEntry, setManualEntry] = useState(defaultManualEntry);
  const [addingManual, setAddingManual] = useState(false);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [showCreateCategory, setShowCreateCategory] = useState(false);
  const [editingCustomCategory, setEditingCustomCategory] = useState({});
  const [newCategoryName, setNewCategoryName] = useState('');
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [editingNotesFor, setEditingNotesFor] = useState(null);
  const [tempNotes, setTempNotes] = useState('');
  const [showMoreInfoModal, setShowMoreInfoModal] = useState(false);
  const [selectedAlumniForInfo, setSelectedAlumniForInfo] = useState(null);
  // REMOVED: chatEmailFilter - table now loads all saved alumni directly from Supabase
  const notesTimeoutRef = useRef(null);
  const autoCategorizeAllAlumniRef = useRef(null);
  const editTimeoutsRef = useRef({}); // Store timeouts for each field being edited

  const loadUserCategories = useCallback(async () => {
    if (!userId) return;
    try {
      const response = await apiFetch(`/user_categories?user_id=${userId}`);
      if (response.ok) {
        const data = await response.json();
        const formattedCategories = (data.categories || [])
          .map(category => {
            if (typeof category === 'string') return category;
            if (category && typeof category === 'object') {
              return category.category_name || category.name || '';
            }
            return '';
          })
          .filter(Boolean);
        setUserCategories(formattedCategories);
      }
    } catch (error) {
      console.error('Failed to load user categories:', error);
    }
  }, [userId]);

  const refreshSavedAlumni = useCallback(async (_unusedChatId = null, forceRefresh = false) => {
    const timestamp = new Date().toISOString();
    console.log(`🔄 [${timestamp}] refreshSavedAlumni called - Current userId:`, userId, 'forceRefresh:', forceRefresh);
    
    if (!userId) {
      console.log(`⚠️ [${timestamp}] No userId, clearing saved alumni`);
      setSavedAlumniRows([]);
      return;
    }
    
    try {
      console.log(`📊 [${timestamp}] Querying saved_alumni for userId:`, userId);
      const queryStart = Date.now();
      
      const { data, error } = await supabase
        .from('saved_alumni')
        .select(
          'id, alumni_name, alumni_position, alumni_company, alumni_email, alumni_linkedin, email_subject, email_body, sent_at, category, comments, metadata, saved_by_more_info, chat_id',
        )
        .eq('user_id', userId)
        .is('saved_by_more_info', null)
        .order('id', { ascending: false });
      
      const queryDuration = Date.now() - queryStart;
      console.log(`⏱️ [${timestamp}] Query completed in ${queryDuration}ms`);
      
      if (error) {
        console.error(`❌ [${timestamp}] Error fetching saved alumni:`, error);
        console.error('Error details:', {
          message: error.message,
          code: error.code,
          details: error.details,
          hint: error.hint,
        });
        showToast('Failed to load saved alumni. Please try refreshing.', 'error');
        return;
      }
      
      if (data) {
        console.log(`✅ [${timestamp}] Saved alumni fetched - Count:`, data.length);
        console.log(`📋 [${timestamp}] Saved alumni emails:`, data.map(r => r.alumni_email).filter(Boolean));
        console.log(`📋 [${timestamp}] Full saved alumni data:`, data);
        
        setSavedAlumniRows(data);
        const categoriesData = {};
        const commentsData = {};
        data.forEach(row => {
          if (row.category) categoriesData[row.id] = row.category;
          if (row.comments) commentsData[row.id] = row.comments;
        });
        setCategories(categoriesData);
        setComments(commentsData);
        
        // Removed automatic toast - it was misleading (showed total count, not chat-specific)
        // Toast will only appear for explicit user actions or errors
        
        setTimeout(() => {
          autoCategorizeAllAlumniRef.current?.();
        }, 1000);
      } else {
        console.log(`ℹ️ [${timestamp}] No saved alumni data returned (data is null/undefined)`);
        setSavedAlumniRows([]);
      }
    } catch (error) {
      console.error(`❌ [${timestamp}] Unexpected error fetching saved alumni:`, error);
      console.error('Error stack:', error.stack);
      showToast('Unexpected error loading saved alumni. Please try again.', 'error');
    }
  }, [userId, showToast]);

  // Clear saved alumni when user signs out
  useEffect(() => {
    if (!userId) {
      setSavedAlumniRows([]);
    }
  }, [userId]);

  const saveAlumni = useCallback(
    async ({
      alumni_name,
      alumni_position,
      alumni_company,
      alumni_email,
      alumni_linkedin,
      email_subject,
      email_body,
      location,
      metadata,
      chat_id,
    }) => {
      console.log('💾 saveAlumni called - Current userId:', userId, 'chat_id:', chat_id);
      console.log('💾 Saving alumni:', { alumni_name, alumni_email, alumni_company, chat_id });
      if (!userId) {
        console.error('❌ No userId in saveAlumni');
        return null;
      }
      try {
        if (ensureUserRecordExists) {
          const userExists = await ensureUserRecordExists(userId);
          if (!userExists) {
            showToast('Failed to verify user profile. Please try again.', 'error');
            return null;
          }
        }

        const insertObj = {
          user_id: userId,
          was_sent: false,
        };
        if (chat_id) insertObj.chat_id = chat_id;
        if (alumni_name !== undefined) insertObj.alumni_name = alumni_name;
        if (alumni_position !== undefined) insertObj.alumni_position = alumni_position;
        if (alumni_company !== undefined) insertObj.alumni_company = alumni_company;
        if (alumni_email !== undefined) insertObj.alumni_email = alumni_email;
        if (alumni_linkedin !== undefined) insertObj.alumni_linkedin = alumni_linkedin;
        if (email_subject !== undefined) insertObj.email_subject = email_subject;
        if (email_body !== undefined) insertObj.email_body = email_body;
        if (location) {
          insertObj.metadata = { ...(insertObj.metadata || {}), location };
        }
        if (metadata && Object.keys(metadata).length > 0) {
          insertObj.metadata = { ...(insertObj.metadata || {}), ...metadata };
        }

        console.log('💾 Inserting/updating alumni with userId:', userId, 'chat_id:', chat_id, 'insertObj:', insertObj);
        // CRITICAL: Update unique constraint to include chat_id so same email can be saved in different chats
        const { data, error } = await supabase
          .from('saved_alumni')
          .upsert(insertObj, { onConflict: chat_id ? 'user_id,alumni_email,chat_id' : 'user_id,alumni_email' })
          .select();
        if (error) {
          console.error('❌ Error saving alumni:', error);
          showToast('Failed to save alumni: ' + error.message, 'error');
          return null;
        }

        const savedRow = data?.[0];
        console.log('✅ Alumni saved successfully:', savedRow);

        if (savedRow && alumni_position && alumni_company) {
          try {
            const response = await apiFetch(`/categorize_alumni?user_id=${userId}`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                alumni_position,
                alumni_company,
              }),
            });
            const categoryData = await response.json();

            if (response.ok && categoryData?.category) {
              await supabase.from('saved_alumni').update({ category: categoryData.category }).eq('id', savedRow.id);
            }
          } catch (error) {
            console.error('Failed to auto-categorize alumni:', error);
          }
        }

        // Refresh saved alumni so the table stays in sync
        await refreshSavedAlumni(chat_id);
        return savedRow;
      } catch (error) {
        showToast('Failed to save alumni: ' + error.message, 'error');
        console.error(error);
        return null;
      }
    },
    [ensureUserRecordExists, refreshSavedAlumni, showToast, userId],
  );

  useEffect(() => {
    if (userId) {
      loadUserCategories();
    }
  }, [loadUserCategories, userId]);

  useEffect(() => {
    if (!openFilterDropdown) return;
    const handleClickOutside = event => {
      const filterDropdown = document.querySelector('[data-filter-dropdown]');
      if (filterDropdown && !filterDropdown.contains(event.target)) {
        const filterButton = event.target.closest('button[data-filter-button]');
        if (!filterButton) {
          setOpenFilterDropdown(null);
          setPendingFilters({});
        }
      }
    };
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openFilterDropdown, setOpenFilterDropdown, setPendingFilters]);

  useEffect(() => {
    if (!showCategoryDropdown) return;
    const handleClickOutside = event => {
      if (!event.target.closest('.category-dropdown')) {
        setShowCategoryDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [setShowCategoryDropdown, showCategoryDropdown]);

  const createCustomCategory = useCallback(async () => {
    if (!newCategoryName.trim() || !userId) return;
    try {
      const response = await apiFetch(`/user_categories?user_id=${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ category_name: newCategoryName.trim() }),
      });
      if (response.ok) {
        setNewCategoryName('');
        setShowCreateCategory(false);
        await loadUserCategories();
      } else {
        const error = await response.json();
        showToast(error.detail || 'Failed to create category.', 'error');
      }
    } catch (error) {
      showToast('Failed to create category.', 'error');
    }
  }, [loadUserCategories, newCategoryName, showToast, userId]);

  const updateAlumniCategory = useCallback(
    async (alumniId, category) => {
      if (!userId) return;
      try {
        const response = await apiFetch(`/saved_alumni/${alumniId}/category?user_id=${userId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ category }),
        });
        if (response.ok) {
          setCategories(prev => ({ ...prev, [alumniId]: category }));
          await loadUserCategories();
        } else {
          showToast('Failed to update category.', 'error');
        }
      } catch (error) {
        showToast('Failed to update category.', 'error');
      }
    },
    [loadUserCategories, showToast, userId],
  );

  const deleteCategory = useCallback(
    async categoryName => {
      if (!userId) return;
      if (
        !window.confirm(
          `Are you sure you want to delete the category "${categoryName}"? All alumni in this category will be moved to "None".`,
        )
      ) {
        return;
      }
      try {
        const response = await apiFetch(`/user_categories/${encodeURIComponent(categoryName)}?user_id=${userId}`, {
          method: 'DELETE',
        });
        if (response.ok) {
          setCategories(prev => {
            const next = { ...prev };
            Object.keys(next).forEach(id => {
              if (next[id] === categoryName) {
                next[id] = null;
              }
            });
            return next;
          });
          await loadUserCategories();
        } else {
          showToast('Failed to delete category.', 'error');
        }
      } catch (error) {
        showToast('Failed to delete category.', 'error');
      }
    },
    [loadUserCategories, showToast, userId],
  );

  const handleManualEntry = useCallback(
    async event => {
      event.preventDefault();
      if (!manualEntry.name || !manualEntry.company) {
        showToast('Name and Company are required', 'error');
        return;
      }
      setAddingManual(true);
      try {
        await saveAlumni({
          alumni_name: manualEntry.name,
          alumni_position: manualEntry.position,
          alumni_company: manualEntry.company,
          location: manualEntry.location,
          alumni_email: manualEntry.email,
          alumni_linkedin: manualEntry.linkedin,
          chat_id: currentChatId || null,
        });
        setManualEntry(defaultManualEntry);
        setShowManualEntry(false);
        showToast('Alumni added successfully!', 'success');
      } catch (error) {
        showToast('Failed to add alumni: ' + error.message, 'error');
      }
      setAddingManual(false);
    },
    [manualEntry, saveAlumni, showToast, currentChatId],
  );

  const toggleManualEntry = useCallback(() => {
    setShowManualEntry(prev => !prev);
    if (showManualEntry) {
      setManualEntry(defaultManualEntry);
    }
  }, [showManualEntry]);

  const handleManualEntryChange = useCallback((field, value) => {
    setManualEntry(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleSort = useCallback(
    column => {
      if (sortColumn === column) {
        setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortColumn(column);
        setSortDirection('asc');
      }
    },
    [sortColumn],
  );

  const getSortValue = useCallback(
    (row, column) => {
      switch (column) {
        case 'name':
          return (row.alumni_name || '').toLowerCase();
        case 'position':
          return (row.alumni_position || 'N/A').toLowerCase();
        case 'company':
          return (row.alumni_company || 'N/A').toLowerCase();
        case 'category':
          return (categories[row.id] || 'None').toLowerCase();
        default:
          return '';
      }
    },
    [categories],
  );

  const applyFilters = useCallback(
    rows => {
      return rows.filter(row => {
        for (const [column, selectedValues] of Object.entries(filters)) {
          if (!selectedValues || selectedValues.length === 0) continue;
          let value;
          switch (column) {
            case 'position':
              value = row.alumni_position || 'N/A';
              break;
            case 'company':
              value = row.alumni_company || 'N/A';
              break;
            case 'category':
              value = categories[row.id] || 'None';
              break;
            default:
              continue;
          }
          if (!selectedValues.includes(value)) {
            return false;
          }
        }
        return true;
      });
    },
    [categories, filters],
  );

  const applySorting = useCallback(
    rows => {
      if (!sortColumn) return rows;
      const sorted = [...rows].sort((a, b) => {
        const aVal = getSortValue(a, sortColumn);
        const bVal = getSortValue(b, sortColumn);
        return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      });
      return sorted;
    },
    [getSortValue, sortColumn, sortDirection],
  );

  const clearAllFilters = useCallback(() => {
    setFilters({});
    setSortColumn(null);
    setSortDirection('asc');
    setOpenFilterDropdown(null);
    setPendingFilters({});
  }, []);

  const getActiveFilterCount = useCallback(() => {
    return Object.values(filters).reduce((sum, arr) => sum + (arr?.length || 0), 0);
  }, [filters]);

  const getUniqueValuesWithCounts = useCallback(
    (rows, column) => {
      const valueCounts = new Map();
      rows.forEach(row => {
        let value = '';
        switch (column) {
          case 'position':
            value = row.alumni_position || 'N/A';
            break;
          case 'company':
            value = row.alumni_company || 'N/A';
            break;
          case 'category':
            value = categories[row.id] || 'None';
            break;
          default:
            value = '';
        }
        if (value) {
          valueCounts.set(value, (valueCounts.get(value) || 0) + 1);
        }
      });
      return Array.from(valueCounts.entries())
        .map(([value, count]) => ({ value, count }))
        .sort((a, b) => a.value.localeCompare(b.value));
    },
    [categories],
  );

  const getFilteredRowsExcluding = useCallback(
    (rows, excludeColumn) => {
      return rows.filter(row => {
        for (const [column, selectedValues] of Object.entries(filters)) {
          if (column === excludeColumn || !selectedValues || selectedValues.length === 0) continue;
          let value;
          switch (column) {
            case 'position':
              value = row.alumni_position || 'N/A';
              break;
            case 'company':
              value = row.alumni_company || 'N/A';
              break;
            case 'category':
              value = categories[row.id] || 'None';
              break;
            default:
              continue;
          }
          if (!selectedValues.includes(value)) {
            return false;
          }
        }
        return true;
      });
    },
    [categories, filters],
  );

  const openNotesModal = useCallback((alumniId, currentNotes = '') => {
    setEditingNotesFor(alumniId);
    setTempNotes(currentNotes);
    setShowNotesModal(true);
  }, []);

  const closeNotesModal = useCallback(() => {
    setShowNotesModal(false);
    setEditingNotesFor(null);
    setTempNotes('');
  }, []);

  const handleNotesChange = useCallback(
    value => {
      setTempNotes(value);
      if (notesTimeoutRef.current) {
        clearTimeout(notesTimeoutRef.current);
      }
      if (!editingNotesFor) return;
      notesTimeoutRef.current = setTimeout(async () => {
        setComments(prev => ({ ...prev, [editingNotesFor]: value }));
        await supabase.from('saved_alumni').update({ comments: value }).eq('id', editingNotesFor);
      }, 1000);
    },
    [editingNotesFor],
  );

  const handleFieldUpdate = useCallback(
    async (alumniId, field, value) => {
      // Update local state immediately
      setSavedAlumniRows(prev =>
        prev.map(row => (row.id === alumniId ? { ...row, [field]: value } : row))
      );

      // Clear existing timeout for this field
      const timeoutKey = `${alumniId}_${field}`;
      if (editTimeoutsRef.current[timeoutKey]) {
        clearTimeout(editTimeoutsRef.current[timeoutKey]);
      }

      // Set new timeout to save to Supabase
      editTimeoutsRef.current[timeoutKey] = setTimeout(async () => {
        try {
          await supabase.from('saved_alumni').update({ [field]: value }).eq('id', alumniId);
          delete editTimeoutsRef.current[timeoutKey];
        } catch (error) {
          console.error(`Error updating ${field}:`, error);
          showToast(`Failed to update ${field}`, 'error');
        }
      }, 1000);
    },
    [showToast],
  );

  const openMoreInfoModal = useCallback(
    async alumni => {
      if (!userId) return;
      let alumniWithMetadata = alumni;
      
      // Check if this is a saved_alumni row (has alumni_email, alumni_name, etc.) or a search result candidate (has jobs_email, name, etc.)
      const isSavedAlumniRow = alumni.alumni_email !== undefined;
      const emailToLookup = isSavedAlumniRow ? alumni.alumni_email : (alumni.jobs_email || '');
      
      try {
        // If this is already a saved_alumni row, parse metadata and use it directly
        if (isSavedAlumniRow) {
          // Parse metadata if it's a JSON string (as stored in Supabase)
          let parsedMetadata = {};
          if (alumni.metadata) {
            if (typeof alumni.metadata === 'string') {
              try {
                parsedMetadata = JSON.parse(alumni.metadata);
              } catch (e) {
                console.error('Error parsing metadata JSON:', e);
                parsedMetadata = {};
              }
            } else {
              parsedMetadata = alumni.metadata;
            }
          }
          
          // Map saved_alumni row fields to alumni object structure for the modal
          // Most fields come from the row, but some MUST come from metadata:
          // - degree must come from metadata.school_degree
          // - Previous job (jobs_1_*) must come from metadata
          alumniWithMetadata = {
            name: alumni.alumni_name,
            new_name: parsedMetadata.new_name || alumni.alumni_name,
            vector_text: parsedMetadata.vector_text,
            graduation_year: parsedMetadata.graduation_year,
            school_degree: parsedMetadata.school_degree,
            degree: parsedMetadata.school_degree, // degree MUST come from metadata.school_degree
            education_0_school: parsedMetadata.education_0_school,
            jobs_0_title: parsedMetadata.jobs_0_title || alumni.alumni_position,
            jobs_0_function: parsedMetadata.jobs_0_function,
            jobs_0_duration: parsedMetadata.jobs_0_duration,
            jobs_0_company_tenure: parsedMetadata.jobs_0_company_tenure,
            jobs_0_company_industry: parsedMetadata.jobs_0_company_industry,
            jobs_0_company_domain: parsedMetadata.jobs_0_company_domain,
            jobs_0_company_type: parsedMetadata.jobs_0_company_type,
            jobs_0_company_employee_count: parsedMetadata.jobs_0_company_employee_count,
            jobs_1_title: parsedMetadata.jobs_1_title, // MUST come from metadata
            jobs_1_company_name: parsedMetadata.jobs_1_company_name, // MUST come from metadata
            metadata: parsedMetadata,
          };
        } else if (emailToLookup) {
          // Try to fetch existing metadata from saved_alumni table
          const { data: existingData, error } = await supabase
            .from('saved_alumni')
            .select('metadata')
            .eq('user_id', userId)
            .eq('alumni_email', emailToLookup)
            .single();
          if (!error && existingData?.metadata) {
            alumniWithMetadata = { ...alumni, metadata: existingData.metadata };
          } else {
            // CRITICAL: Include chat_id even for more_info saves
            // Note: These are filtered out in queries, but should still be linked to chat
            const insertObj = {
              user_id: userId,
              alumni_name: alumni.name || alumni.alumni_name || '',
              alumni_position: alumni.current_position || alumni.alumni_position || '',
              alumni_company: alumni.company_raw || alumni.alumni_company || '',
              alumni_email: emailToLookup,
              alumni_linkedin: alumni.linkedin_url || alumni.alumni_linkedin || '',
              saved_by_more_info: true,
              chat_id: currentChatId || null, // Link to current chat if available
              metadata: {
                vector_text: alumni.vector_text,
                new_name: alumni.new_name,
                graduation_year: alumni.graduation_year,
                school_degree: alumni.school_degree,
                degree: alumni.degree,
                education_0_school: alumni.education_0_school,
                jobs_0_title: alumni.jobs_0_title,
                jobs_0_function: alumni.jobs_0_function,
                jobs_0_duration: alumni.jobs_0_duration,
                jobs_0_company_tenure: alumni.jobs_0_company_tenure,
                jobs_0_company_industry: alumni.jobs_0_company_industry,
                jobs_0_company_domain: alumni.jobs_0_company_domain,
                jobs_0_company_type: alumni.jobs_0_company_type,
                jobs_0_company_employee_count: alumni.jobs_0_company_employee_count,
                jobs_1_title: alumni.jobs_1_title,
                jobs_1_company_name: alumni.jobs_1_company_name,
                university: alumni.university,
                university_display: alumni.university_display,
              },
            };
            // Update conflict resolution to include chat_id if available
            const conflictKey = currentChatId ? 'user_id,alumni_email,chat_id' : 'user_id,alumni_email';
            await supabase.from('saved_alumni').upsert(insertObj, { onConflict: conflictKey }).select();
          }
        }
      } catch (error) {
        console.error('Error in openMoreInfoModal:', error);
      }
      setSelectedAlumniForInfo(alumniWithMetadata);
      setShowMoreInfoModal(true);
    },
    [userId, currentChatId],
  );

  // REMOVED: updateChatEmailFilter and chatEmailFilter - savedAlumniRows now contains
  // all alumni for the user, and downstream consumers handle any chat-specific filtering

  const filteredTableRows = useMemo(() => applyFilters(savedAlumniRows), [applyFilters, savedAlumniRows]);
  const sortedTableRows = useMemo(() => applySorting(filteredTableRows), [applySorting, filteredTableRows]);
  const totalCount = savedAlumniRows.length;
  const filteredCount = filteredTableRows.length;
  const activeFilterCount = useMemo(() => Object.values(filters).reduce((sum, arr) => sum + (arr?.length || 0), 0), [filters]);

  const closeMoreInfoModal = useCallback(() => {
    setShowMoreInfoModal(false);
    setSelectedAlumniForInfo(null);
  }, []);

  const handleRemoveAlumni = useCallback(
    async row => {
      await supabase.from('saved_alumni').delete().eq('id', row.id);
      setSavedAlumniRows(prev => prev.filter(a => a.id !== row.id));
    },
    [],
  );

  const autoCategorizeAllAlumni = useCallback(async () => {
    if (!userId) return;
    const alumniWithoutCategories = savedAlumniRows.filter(row => !categories[row.id]);
    if (alumniWithoutCategories.length === 0) {
      return;
    }
    for (const row of alumniWithoutCategories) {
      try {
        const response = await apiFetch(`/categorize_alumni?user_id=${userId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            alumni_position: row.alumni_position,
            alumni_company: row.alumni_company,
          }),
        });
        const data = await response.json();
        if (response.ok) {
          setCategories(prev => ({ ...prev, [row.id]: data.category }));
          await supabase.from('saved_alumni').update({ category: data.category }).eq('id', row.id);
        }
      } catch (error) {
        console.error(`Failed to categorize alumni ${row.id}:`, error);
      }
    }
    await loadUserCategories();
  }, [categories, loadUserCategories, savedAlumniRows, userId]);

  autoCategorizeAllAlumniRef.current = autoCategorizeAllAlumni;

  const toggleCategoryDropdown = useCallback(() => {
    setShowCategoryDropdown(prev => !prev);
  }, []);

  const toggleCellExpansion = useCallback((rowId, column) => {
    const key = `${rowId}_${column}`;
    setExpandedCells(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  }, []);

  const clearSavedAlumniData = useCallback(() => {
    setSavedAlumniRows([]);
    setUserCategories([]);
    setCategories({});
    setComments({});
    setExpandedCells({});
    setSelectedCategoryFilter('all');
    setFilters({});
    setOpenFilterDropdown(null);
    setPendingFilters({});
    setSortColumn(null);
    setSortDirection('asc');
    setManualEntry(defaultManualEntry);
    setShowManualEntry(false);
    setShowCreateCategory(false);
    setEditingCustomCategory({});
    setNewCategoryName('');
    setShowCategoryDropdown(false);
    setShowNotesModal(false);
    setEditingNotesFor(null);
    setTempNotes('');
    setShowMoreInfoModal(false);
    setSelectedAlumniForInfo(null);
  }, []);

  return {
    savedAlumniRows,
    setSavedAlumniRows,
    userCategories,
    categories,
    comments,
    setComments,
    expandedCells,
    selectedCategoryFilter,
    setSelectedCategoryFilter,
    showMoreInfoModal,
    selectedAlumniForInfo,
    openMoreInfoModal,
    closeMoreInfoModal,
    manualEntry,
    addingManual,
    showManualEntry,
    toggleManualEntry,
    handleManualEntry,
    handleManualEntryChange,
    showCreateCategory,
    setShowCreateCategory,
    editingCustomCategory,
    setEditingCustomCategory,
    newCategoryName,
    setNewCategoryName,
    showCategoryDropdown,
    setShowCategoryDropdown,
    toggleCategoryDropdown,
    toggleCellExpansion,
    sortColumn,
    sortDirection,
    filters,
    setFilters,
    openFilterDropdown,
    setOpenFilterDropdown,
    pendingFilters,
    setPendingFilters,
    handleSort,
    applyFilters,
    applySorting,
    clearAllFilters,
    getActiveFilterCount,
    getUniqueValuesWithCounts,
    getFilteredRowsExcluding,
    filteredTableRows,
    sortedTableRows,
    totalCount,
    filteredCount,
    activeFilterCount,
    showNotesModal,
    editingNotesFor,
    tempNotes,
    handleNotesChange,
    handleFieldUpdate,
    openNotesModal,
    closeNotesModal,
    createCustomCategory,
    updateAlumniCategory,
    deleteCategory,
    handleRemoveAlumni,
    refreshSavedAlumni,
    saveAlumni,
    clearSavedAlumniData,
    // REMOVED: updateChatEmailFilter - no longer needed
  };
};

export { useConnectCRM };
